import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";
import type { CustomerProfileDto } from "./useCustomerProfile";
import { legalAcceptanceDocuments } from "../../pages/legal/legal-content";

interface AcceptLegalDocumentRequest {
  documentSlug: string;
  version: string;
  agreed: boolean;
}

interface AcceptLegalDocumentsResponse {
  termsAcceptedVersion: string | null;
}

/**
 * Records the customer's acceptance of the legal documents the gate showed them.
 *
 * Every document the dialog presented is sent, not only the Terms — showing them
 * is itself the obligation, and a record naming only the Terms could not later say
 * which privacy notice this customer was given. Which of them the customer agreed
 * to, as opposed to was shown, travels with each one.
 *
 * On success the cached profile is patched in place rather than invalidated. The
 * gate is blocking on this value, so a refetch would put the customer back on a
 * spinner immediately after they clicked Accept.
 */
export function useAcceptLegalDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const documents: AcceptLegalDocumentRequest[] = legalAcceptanceDocuments.map((doc) => ({
        documentSlug: doc.slug,
        version: doc.version,
        agreed: doc.agreed,
      }));

      const res = await apiClient.post<AcceptLegalDocumentsResponse>(
        "/customers/me/legal-documents/accept",
        { documents }
      );
      return res.data;
    },
    onSuccess: (data) => {
      const patch = (previous: CustomerProfileDto | undefined) =>
        previous
          ? { ...previous, termsAcceptedVersion: data.termsAcceptedVersion }
          : previous;

      // Both caches hold the same shape and the gate reads the provision one, so
      // leaving either stale would re-show the dialog on the next render.
      queryClient.setQueryData<CustomerProfileDto>(["customer-provision"], patch);
      queryClient.setQueryData<CustomerProfileDto>(["customer-profile"], patch);
    },
  });
}
